class ThemeDrawers extends HTMLElement {
  constructor() {
    super();

    this.activeDrawer = null;
    this.previousTrigger = null;
    this.closeTimer = null;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  motionDuration(name, fallback) {
    const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    if (!value) return fallback;
    const parsed = Number.parseFloat(value);
    if (!Number.isFinite(parsed)) return fallback;
    return value.endsWith('ms') ? parsed : parsed * 1000;
  }

  connectedCallback() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    this.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onKeydown);
    this.removeEventListener('change', this.onChange);
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
  }

  onDocumentClick(event) {
    const trigger = event.target.closest('[data-drawer-open]');

    if (trigger) {
      event.preventDefault();
      this.open(trigger.dataset.drawerOpen, trigger);
      return;
    }

    if (event.target.closest('[data-drawer-close]')) {
      event.preventDefault();
      this.close();
      return;
    }

    if (event.target.closest('[data-menu-drawer-back]')) {
      event.preventDefault();
      this.activeDrawer?.querySelector('theme-navigation')?.closeSubmenu();
    }
  }

  onKeydown(event) {
    if (event.key === 'Escape' && this.activeDrawer) {
      const navigation = this.activeDrawer.querySelector('theme-navigation');
      if (navigation?.activeSubmenu) {
        event.preventDefault();
        navigation.closeSubmenu();
        return;
      }
      this.close();
    }

    if (event.key === 'Tab' && this.activeDrawer) {
      this.trapFocus(event);
    }
  }

  onChange(event) {
    const form = event.target.closest('.drawer-menu__localization-form');
    if (!form || !this.contains(form)) return;

    const selector = event.target.closest('select[name="country_code"], select[name="locale_code"]');
    if (!selector) return;

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.submit();
  }

  open(drawerName, trigger) {
    const drawer = this.querySelector(`[data-drawer="${drawerName}"]`);

    if (!drawer) {
      return;
    }

    const previousDrawer = this.activeDrawer;
    if (previousDrawer === drawer) {
      return;
    }

    this.close(false);
    clearTimeout(this.closeTimer);
    this.closeTimer = null;
    if (previousDrawer && previousDrawer !== drawer) {
      previousDrawer.hidden = true;
    }
    this.activeDrawer = drawer;
    this.previousTrigger = trigger;
    this.setAttribute('data-open', drawerName);
    drawer.hidden = false;
    drawer.setAttribute('aria-hidden', 'false');
    trigger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('drawer-open');

    requestAnimationFrame(() => {
      drawer.setAttribute('data-active', '');
      this.focusFirstElement(drawer);
    });
  }

  close(restoreFocus = true) {
    if (!this.activeDrawer) {
      return;
    }

    const drawer = this.activeDrawer;
    drawer.querySelectorAll('theme-navigation').forEach((navigation) => {
      navigation.dispatchEvent(new CustomEvent('navigation:reset'));
    });
    drawer.removeAttribute('data-active');
    drawer.setAttribute('aria-hidden', 'true');
    this.removeAttribute('data-open');
    document.documentElement.classList.remove('drawer-open');
    this.activeDrawer = null;

    if (this.previousTrigger) {
      this.previousTrigger.setAttribute('aria-expanded', 'false');
    }

    clearTimeout(this.closeTimer);
    const closeDelay = this.prefersReducedMotion() ? 1 : this.motionDuration('--motion-duration-overlay', 320);
    this.closeTimer = setTimeout(() => {
      drawer.hidden = true;
      this.closeTimer = null;
    }, closeDelay);

    if (restoreFocus && this.previousTrigger) {
      this.previousTrigger.focus();
    }
  }

  focusFirstElement(drawer) {
    const focusable = this.getFocusableElements(drawer);

    if (focusable.length > 0) {
      focusable[0].focus();
    }
  }

  trapFocus(event) {
    const focusable = this.getFocusableElements(this.activeDrawer);

    if (focusable.length === 0) {
      event.preventDefault();
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    }

    if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  getFocusableElements(container) {
    return Array.from(
      container.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => element.offsetParent !== null);
  }
}

if (!customElements.get('theme-drawers')) {
  customElements.define('theme-drawers', ThemeDrawers);
}
